/**
 * DirectorOverview.tsx - Resumen Clínico del director (`director_overview`)
 *
 * Landing screen del rol director — KPIs agregados de TODA la clínica
 * (operación + crecimiento de pacientes). Separado de `DashboardOverview`
 * (vista personal del terapeuta, sin métricas) y de `CashFlowView` (Finanzas).
 *
 * Fase 1 (docs/spec_dashboard_director_bi.md §9): KPIs reales vía RPC, rango
 * fijo al mes actual — completada.
 *
 * Fase 2 (§6, §9) — completada en este archivo:
 * - Selector de período: Mes / Trimestre / Año / Personalizado (`PeriodSelector`).
 * - Comparación automática vs. período anterior inmediato (2da llamada a la RPC).
 * - Checkbox opcional "vs. año anterior" (YoY, 3ra llamada a la RPC) — deltas
 *   de ambas comparaciones se muestran a la vez en cada KPI card (`trend`).
 * - Las 2-3 llamadas van en paralelo (Promise.all), nunca secuenciales.
 * - Estado 100% local (useState) — sin querystring, por decisión del spec.
 *
 * Fase 3 (§6, §9) — completada en este archivo:
 * - Selector de terapeuta (drill-down) en la misma barra de filtros que
 *   `PeriodSelector` — opción "Todos los terapeutas" (default, agregado
 *   clínica) + lista de terapeutas activos (`terapeutas` donde `activo=true`,
 *   select simple, sin RPC nueva — es lectura llana permitida al director).
 * - `p_terapeuta_id` se pasa a las 2-3 llamadas existentes de
 *   `get_director_dashboard_stats` (actual + anterior inmediato + YoY
 *   opcional) — la RPC ya recalculaba todo scoped a un terapeuta desde
 *   Fase 1, acá solo se conecta el selector al parámetro.
 * - Las tablas "Ocupación por terapeuta" y "Pacientes por terapeuta" no se
 *   ocultan con terapeuta seleccionado — muestran la única fila que la RPC
 *   ya filtra, sin lógica adicional en el frontend.
 *
 * Fase 4 (§8, §9) — completada en este archivo:
 * - Gráfico de línea "Consultas por semana" (`WeeklyConsultasChart`, recharts
 *   `LineChart`) usando el campo nuevo `operacionales.consultas_por_semana`
 *   (sql/migrations/add_director_dashboard_serie_semanal_2026_07_05.sql —
 *   aplicada en Supabase el 2026-07-05, verificada con datos reales en
 *   browser). El campo se sigue leyendo con `?? []`, mismo criterio
 *   defensivo que el resto del archivo.
 * - `BreakdownCard` (bar horizontal custom) de "Consultas por tipo" y
 *   "Consultas por modalidad" reemplazados por `PieBreakdownCard` (recharts
 *   `PieChart`) — mismo dato, ahora como torta + lista lateral con %.
 *   `BreakdownCard` NO se eliminó: sigue usándose para "Origen de pacientes
 *   nuevos" (growth), fuera de alcance de esta fase.
 * - Colores de charts: tokens Apple HIG ya usados en KpiCard/BreakdownCard
 *   (`var(--apple-blue)`, `var(--apple-green)`, `var(--apple-orange)`,
 *   `var(--apple-red)`, `var(--apple-purple)`) — ninguna paleta nueva.
 *
 * Fase 5 (§5 D/E, §8, §9) — completada en este archivo, ÚLTIMA fase pendiente
 * del dashboard director BI:
 * - "Ranking terapeutas" (tabla, `SimpleTable`) — nueva RPC
 *   `get_director_therapist_ranking(p_fecha_inicio, p_fecha_fin)`
 *   (sql/migrations/add_director_dashboard_ranking_2026_07_05.sql). Se carga
 *   en su propio `useEffect`, independiente del de `stats/prevStats/yoyStats`:
 *   no tiene comparación vs. período anterior (spec §5 E no la pide) y la RPC
 *   directamente NO acepta `p_terapeuta_id` — es un ranking comparativo de
 *   TODOS los terapeutas activos, así que ignora a propósito el drill-down de
 *   terapeuta de la barra de filtros (ver nota en el propio `useEffect`).
 * - "Marketing / CAC" (`CardPanel` nuevo, al final) — `get_gastos_marketing`
 *   y `get_director_finanzas_stats` (ya en producción desde
 *   add_director_dashboard_bi_2026_07_04.sql), más `upsert_gasto_marketing`
 *   para el formulario "+ Agregar gasto". Igual que el ranking, ninguna de
 *   estas 3 RPC acepta `p_terapeuta_id` — el gasto de marketing es agregado
 *   de TODA la clínica, no se atribuye a un terapeuta, así que esta sección
 *   tampoco reacciona al selector de terapeuta.
 * - CAC (`cac_clinica`) viene ya calculado desde `get_gastos_marketing`. ROI
 *   simple se calcula en el frontend (`ingresos_rango` de
 *   `get_director_finanzas_stats` ÷ `gasto_total_periodo` de
 *   `get_gastos_marketing`) — spec §5 D no pide esa división en SQL.
 *   `gasto_total_periodo`/`cac_clinica` vienen REPETIDOS iguales en cada fila
 *   de `get_gastos_marketing` (agregado clínica, no por canal) y además la
 *   RPC devuelve un ARRAY VACÍO si no hay ningún gasto cargado en el rango
 *   (el `SELECT ... FROM gastos_marketing` no tiene de dónde sacar una fila) —
 *   por eso ambos valores se leen defensivamente de `gastos[0]?.campo` y cae
 *   a "—" vía `fmtCLP`/`fmtRatio` cuando el array viene vacío, mismo criterio
 *   que el resto del archivo con `fmtNum`/`fmtPct`.
 * - Disclaimer de CAC visible en la UI (no solo comentario de código, spec
 *   §5 D lo exige textual): sin tracking UTM por paciente el CAC no es
 *   atribuible a un canal específico.
 * - El período de carga de gasto (formulario, `<input type="month">`) es
 *   SIEMPRE mensual — independiente del `PeriodoModo` general del dashboard
 *   (que puede ser trimestre/año/personalizado). Son dos ejes de tiempo
 *   distintos, no se sincronizan entre sí.
 * - Solo el director puede cargar gastos: ya lo garantiza `upsert_gasto_marketing`
 *   server-side (verifica `rol='director'`) — el frontend no duplica esa
 *   validación, solo muestra un mensaje de error genérico si el submit falla.
 *
 * Con Fase 5, el dashboard director BI (docs/spec_dashboard_director_bi.md)
 * queda completo en `director_overview`. Pendiente fuera de este archivo:
 * - CashFlowView/Finanzas sigue en mock (Fase 6 — fuera de alcance acá).
 *
 * Patrón de referencia: src/components/InvestorPortal/InvestorOverview.tsx
 * (mismo estilo de KPI cards, mismo approach de llamar RPC vía supabase.rpc).
 * Para los charts recharts: src/components/InvestorPortal/InvestorRevenue.tsx
 * (único otro lugar del portal que ya usa recharts — mismo patrón de
 * ResponsiveContainer + Tooltip/Legend con `contentStyle`/`wrapperStyle`).
 *
 * RPC: get_director_dashboard_stats(p_fecha_inicio, p_fecha_fin, p_terapeuta_id)
 * — verifica rol='director' server-side (sql/migrations/add_director_dashboard_bi_2026_07_04.sql,
 * campo `consultas_por_semana` agregado en add_director_dashboard_serie_semanal_2026_07_05.sql).
 * Se llama 2 o 3 veces por render (rango actual + anterior inmediato + YoY
 * opcional) — la RPC ya soporta cualquier rango, la lógica de comparación
 * vive enteramente en este componente (spec §7, nota final).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { supabase } from '../../lib/supabase/client';
import './DirectorOverview.css';

// ============================================================================
// TIPOS — contrato de get_director_dashboard_stats (spec §5 B-C)
// ============================================================================

interface OcupacionTerapeutaRow {
  terapeuta_id: string;
  terapeuta_nombre: string;
  terapeuta_apellido: string;
  completadas: number;
  slots_disponibles: number;
  ocupacion_pct: number | null;
}

interface PacientesTerapeutaRow {
  terapeuta_id: string;
  terapeuta_nombre: string;
  terapeuta_apellido: string;
  total_pacientes: number;
}

// Fase 4 (§8, §9): fila de la serie semanal para el gráfico de línea
// "Consultas por semana" — agregada en add_director_dashboard_serie_semanal_2026_07_05.sql
// dentro de get_director_dashboard_stats. `semana_inicio` es siempre el lunes
// de la semana calendario (date_trunc('week', ...) en Postgres, ISO 8601),
// ya viene ordenado ascendente por semana desde la RPC.
interface ConsultaSemanaRow {
  semana_inicio: string; // YYYY-MM-DD
  total: number;
  completadas: number;
  canceladas: number;
}

// Fase 3 (§6): opción del selector de terapeuta — leída directo de la tabla
// `terapeutas` (sin RPC nueva, spec §6 no la exige — es lectura llana que el
// director ya puede hacer, mismo criterio que otras consultas de catálogo).
interface TerapeutaOption {
  id: string;
  nombre: string;
  apellido: string;
}

interface DirectorDashboardStats {
  meta: {
    fecha_inicio: string;
    fecha_fin: string;
    terapeuta_id: string | null;
  };
  operacionales: {
    total_consultas: number;
    consultas_por_estado: Record<string, number>;
    tasa_cancelacion_pct: number | null;
    tasa_no_show_pct: number | null;
    consultas_por_tipo: Record<string, number>;
    consultas_por_modalidad: Record<string, number>;
    // Fase 4 (§8, §9) — ver ConsultaSemanaRow. Campo nuevo agregado por
    // add_director_dashboard_serie_semanal_2026_07_05.sql (aplicada en
    // Supabase el 2026-07-05) — el resto del código SIEMPRE lee este campo
    // con `?? []` de forma defensiva, mismo criterio que el resto del archivo.
    consultas_por_semana: ConsultaSemanaRow[];
    ocupacion_por_terapeuta: OcupacionTerapeutaRow[];
  };
  growth: {
    pacientes_nuevos: number;
    pacientes_activos_totales: number;
    pacientes_por_terapeuta: PacientesTerapeutaRow[];
    pacientes_referidos_inversionista: number;
    pacientes_organicos: number;
    referidos_inversionista_pct: number | null;
    referidos_organico_pct: number | null;
  };
}

// Fase 5 (§5 E, §9): fila de get_director_therapist_ranking — ranking
// comparativo de TODOS los terapeutas activos del rango. Sin p_terapeuta_id
// en la RPC (ver comentario de cabecera) — nunca se filtra a un solo
// terapeuta, ni siquiera con el drill-down de la barra de filtros activo.
interface TherapistRankingRow {
  terapeuta_id: string;
  terapeuta_nombre: string;
  terapeuta_apellido: string;
  ingresos_periodo: number;
  ranking_ingresos: number;
  completadas: number;
  slots_disponibles: number;
  ocupacion_pct: number | null;
  ranking_ocupacion: number;
  total_consultas_periodo: number;
  canceladas: number;
  no_show: number;
  cancelacion_no_show_pct: number | null;
  ranking_cancelaciones: number;
}

// Fase 5 (§5 D, §9): fila de get_gastos_marketing. `gasto_total_periodo` y
// `cac_clinica` vienen REPETIDOS iguales en cada fila (agregado de TODA la
// clínica en el rango — sin tracking UTM por paciente no se puede atribuir el
// CAC a un canal específico, spec §5 D). Si no hay ningún gasto cargado en el
// rango, la RPC devuelve un array VACÍO (no una fila con estos campos en 0/null)
// — ver nota de cabecera sobre cómo se lee esto defensivamente en el componente.
interface GastoMarketingRow {
  id: string;
  mes: string; // YYYY-MM-DD, normalizado al día 1 del mes
  canal: 'meta_ads' | 'google_ads' | 'organico' | 'otro';
  monto: number;
  notas: string | null;
  registrado_por: string | null;
  gasto_total_periodo: number;
  pacientes_nuevos_periodo: number;
  cac_clinica: number | null;
}

// Fase 5 (§5 D): shape mínimo consumido de get_director_finanzas_stats — esta
// fase SOLO necesita `ingresos_rango` para calcular el ROI simple. El resto de
// sus campos (boletas SII, desglose por método de pago) son de Finanzas/
// CashFlowView (Fase 6), fuera de alcance acá — no se declaran a propósito.
interface DirectorFinanzasStatsSlim {
  ingresos_rango: number | null;
}

// FUNC-083 follow-up: shape de get_pagos_sin_distribucion_director() — 1 fila
// por pago 'pagado' sin fila en distribucion_ingresos (ver
// sql/migrations/func083_reconciliacion_director_2026_09_12.sql).
interface PagoSinDistribucionRow {
  pago_id: string;
  fecha_pago: string | null;
  monto: number;
  monto_final: number | null;
  metodo_pago: string | null;
  consulta_id: string | null;
  paciente_nombre: string | null;
  paciente_apellido: string | null;
  terapeuta_nombre: string | null;
  terapeuta_apellido: string | null;
}

// ============================================================================
// LABELS — mismo criterio que src/components/TherapistConsultations/Consultations.tsx
// ============================================================================

// Nota: no hay mapa de estado acá — el desglose por estado que consume esta
// vista (`consultas_por_estado`) lee claves puntuales (`completada`,
// `cancelada`, `no_asistio`) directo del JSON de la RPC, sin label ni orden.

const TIPO_LABEL: Record<string, string> = {
  individual: 'Individual',
  pareja: 'Pareja',
  familiar: 'Familiar',
};
const TIPO_ORDEN = ['individual', 'pareja', 'familiar'];

const MODALIDAD_LABEL: Record<string, string> = {
  presencial: 'Presencial',
  online: 'Online',
  domicilio: 'Domicilio',
};
const MODALIDAD_ORDEN = ['presencial', 'online', 'domicilio'];

// Fase 5 (§5 D, §7): canales fijos que acepta upsert_gasto_marketing — mismo
// CHECK de la tabla gastos_marketing, replicado acá solo para las opciones del
// <select> (la validación real vive server-side, spec §4).
const CANAL_LABEL: Record<GastoMarketingRow['canal'], string> = {
  meta_ads: 'Meta Ads',
  google_ads: 'Google Ads',
  organico: 'Orgánico',
  otro: 'Otro',
};
const CANAL_ORDEN: GastoMarketingRow['canal'][] = ['meta_ads', 'google_ads', 'organico', 'otro'];

// ============================================================================
// HELPERS
// ============================================================================

const pad2 = (n: number) => String(n).padStart(2, '0');

// Construye YYYY-MM-DD desde componentes locales — evita el corrimiento de
// día que produce Date.toISOString() (convierte a UTC) en huso horario
// negativo como America/Santiago.
const toISODate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Parsea YYYY-MM-DD como medianoche LOCAL (no UTC) — inverso de toISODate.
const parseISODate = (iso: string) => new Date(`${iso}T00:00:00`);

const diffDaysInclusive = (a: Date, b: Date): number =>
  Math.round((b.getTime() - a.getTime()) / 86_400_000) + 1;

// ============================================================================
// SELECTOR DE PERÍODO — Fase 2 (spec §6)
// Constructores de rango parametrizados por fecha ancla: la misma fórmula
// sirve tanto para el rango "actual" (ancla = hoy) como para el "anterior"
// (ancla = hoy corrido hacia atrás) — ver getPreviousRange.
// ============================================================================

type PeriodoModo = 'mes' | 'trimestre' | 'año' | 'personalizado';
type FechaRango = { fecha_inicio: string; fecha_fin: string };

const buildMonthRange = (anchor: Date): FechaRango => {
  const inicio = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const fin = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  return { fecha_inicio: toISODate(inicio), fecha_fin: toISODate(fin) };
};

const buildQuarterRange = (anchor: Date): FechaRango => {
  const q = Math.floor(anchor.getMonth() / 3);
  const inicio = new Date(anchor.getFullYear(), q * 3, 1);
  const fin = new Date(anchor.getFullYear(), q * 3 + 3, 0);
  return { fecha_inicio: toISODate(inicio), fecha_fin: toISODate(fin) };
};

const buildYearRange = (anchor: Date): FechaRango => {
  const inicio = new Date(anchor.getFullYear(), 0, 1);
  const fin = new Date(anchor.getFullYear(), 11, 31);
  return { fecha_inicio: toISODate(inicio), fecha_fin: toISODate(fin) };
};

const getCurrentMonthRange = () => buildMonthRange(new Date());
const getCurrentQuarterRange = () => buildQuarterRange(new Date());
const getCurrentYearRange = () => buildYearRange(new Date());

const MODO_LABEL: Record<PeriodoModo, string> = {
  mes: 'Mes',
  trimestre: 'Trimestre',
  año: 'Año',
  personalizado: 'Personalizado',
};

// Etiqueta del período de comparación INMEDIATO — siempre automático (spec §6).
const PREV_LABEL: Record<PeriodoModo, string> = {
  mes: 'vs. mes anterior',
  trimestre: 'vs. trimestre anterior',
  año: 'vs. año anterior',
  personalizado: 'vs. período anterior',
};

// Etiqueta del período de comparación INTERANUAL — checkbox opcional (spec §6).
const YOY_LABEL: Record<PeriodoModo, string> = {
  mes: 'vs. mismo mes, año pasado',
  trimestre: 'vs. mismo trimestre, año pasado',
  año: 'vs. año anterior', // no se usa en la práctica — ver isYoYRedundant
  personalizado: 'vs. mismo rango, año pasado',
};

// En modo Año, el "período anterior inmediato" YA es el año calendario
// completo anterior — es decir, matemáticamente IDÉNTICO al rango que
// produciría el checkbox YoY (mismas fecha_inicio/fecha_fin corridas -1 año).
// Mostrar ambos sería una 3ra llamada a la RPC redundante con un delta
// duplicado en pantalla, así que el checkbox se deshabilita en este modo.
const isYoYRedundant = (modo: PeriodoModo) => modo === 'año';

// Rango del período anterior INMEDIATO según el modo (spec §6):
// Mes → mes anterior · Trimestre → trimestre anterior · Año → año anterior
// Personalizado → mismo número de días, corrido hacia atrás (spec, textual).
const getPreviousRange = (modo: PeriodoModo, current: FechaRango): FechaRango => {
  const inicio = parseISODate(current.fecha_inicio);
  switch (modo) {
    case 'mes':
      return buildMonthRange(new Date(inicio.getFullYear(), inicio.getMonth() - 1, 1));
    case 'trimestre':
      return buildQuarterRange(new Date(inicio.getFullYear(), inicio.getMonth() - 3, 1));
    case 'año':
      return buildYearRange(new Date(inicio.getFullYear() - 1, 0, 1));
    case 'personalizado': {
      const fin = parseISODate(current.fecha_fin);
      const dias = diffDaysInclusive(inicio, fin);
      const finAnterior = new Date(inicio);
      finAnterior.setDate(finAnterior.getDate() - 1);
      const inicioAnterior = new Date(finAnterior);
      inicioAnterior.setDate(inicioAnterior.getDate() - (dias - 1));
      return { fecha_inicio: toISODate(inicioAnterior), fecha_fin: toISODate(finAnterior) };
    }
  }
};

// Rango interanual (YoY): mismas fecha_inicio/fecha_fin, un año atrás.
// Válido para los 4 modos por igual (no hace falta un caso especial por modo,
// a diferencia de getPreviousRange) — spec §6: "mismo período, año pasado".
// Limitación conocida y aceptada: 29-feb en año bisiesto corrido a año no
// bisiesto lo normaliza el propio `Date` a 1-mar — caso extremo de 1 día
// cada 4 años, no se corrige acá.
const getYoYRange = (current: FechaRango): FechaRango => {
  const inicio = parseISODate(current.fecha_inicio);
  const fin = parseISODate(current.fecha_fin);
  return {
    fecha_inicio: toISODate(new Date(inicio.getFullYear() - 1, inicio.getMonth(), inicio.getDate())),
    fecha_fin: toISODate(new Date(fin.getFullYear() - 1, fin.getMonth(), fin.getDate())),
  };
};

const fmtPct = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`;

const fmtNum = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : new Intl.NumberFormat('es-CL').format(v);

// Fase 5 (§5 D): mismo formateador `fmtCLP` que ya usan CobrosView.tsx e
// InvestorOverview/InvestorRevenue en este portal — se replica acá (no se
// importa de otro archivo, ninguno de esos exporta el helper) para mantener
// el mismo criterio de moneda en todo `DirectorPortal`.
const fmtCLP = (v: number | null | undefined): string =>
  v === null || v === undefined
    ? '—'
    : new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(v);

// Fase 5 (§5 D): ROI simple = ingresos ÷ gasto marketing del período — se
// muestra como múltiplo ("3.50x", "por cada $1 gastado volvieron $3.50"), no
// como % relativo: el spec define la fórmula como una división simple, un
// múltiplo es más legible para ese significado que un porcentaje.
const fmtRatio = (v: number | null | undefined): string =>
  v === null || v === undefined || !Number.isFinite(v) ? '—' : `${v.toFixed(2)}x`;

// Fase 5 (§5 D): etiqueta de mes para la tabla de gastos — mismo idioma que
// `periodoLabel` (modo 'mes') más abajo: `toLocaleDateString` + capitalizar
// la primera letra a mano (Intl en es-CL siempre devuelve el mes en minúscula).
const fmtMesLabel = (iso: string): string => {
  const label = parseISODate(iso).toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// Fase 4 (§8): eje X del gráfico "Consultas por semana" — formato corto tipo
// "29 jun" (día numérico + mes abreviado, sin punto). `{ day: 'numeric' }` en
// vez de `'2-digit'`: en es-CL, Intl separa día/mes con un espacio solo con
// día numérico ("29 jun") — con `'2-digit'` inserta un guion ("29-jun"), que
// no es el formato pedido por el spec. El `.replace(/\.$/, '')` es defensivo:
// algunas versiones de ICU agregan un punto final al mes abreviado ("jun.")
// que acá no queremos en un eje de gráfico.
const fmtSemanaCorta = (iso: string): string =>
  parseISODate(iso).toLocaleDateString('es-CL', { day: 'numeric', month: 'short' }).replace(/\.$/, '');

// ============================================================================
// COLORES DE GRÁFICOS (recharts) — Fase 4, spec §9
// Reutiliza EXACTAMENTE los mismos tokens Apple HIG que ya usan KpiCard y
// BreakdownCard en este archivo (var(--apple-blue/green/orange/red/purple)) —
// ninguna paleta nueva, mismo criterio de consistencia visual que el resto
// del portal. Los navegadores modernos resuelven var(--...) en atributos de
// presentación SVG (fill/stroke) igual que en propiedades CSS estándar.
// ============================================================================

// Línea "Consultas por semana": mismo significado semántico que las KPI cards
// de arriba — azul = total (informativo), verde = completadas (éxito),
// rojo = canceladas (negativo). No se grafica no-show: la propia RPC no la
// incluye en la serie semanal (spec §8 solo pide total/completadas/canceladas).
const SEMANA_LINE_COLORS = {
  total: 'var(--apple-blue)',
  completadas: 'var(--apple-green)',
  canceladas: 'var(--apple-red)',
} as const;

// "Consultas por tipo" (4 categorías, TIPO_ORDEN) — sin significado de
// éxito/error, así que no se reutiliza semántica verde=bueno/rojo=malo acá;
// simplemente 4 colores distintos y consistentes con la paleta del portal.
const TIPO_PIE_COLORS = ['var(--apple-blue)', 'var(--apple-purple)', 'var(--apple-orange)', 'var(--apple-green)'];

// "Consultas por modalidad" (2 categorías, MODALIDAD_ORDEN) — se evita a
// propósito verde/rojo (cargarían una connotación de bueno/malo que
// presencial/online no tiene).
const MODALIDAD_PIE_COLORS = ['var(--apple-blue)', 'var(--apple-purple)'];

// ============================================================================
// DELTA / TENDENCIA vs. período de comparación (spec §6, §9 Fase 2)
// ============================================================================

interface TrendEntry {
  value: number;                 // delta con signo — su interpretación depende de `suffix`
  suffix: string;                // ' pp' (puntos porcentuales) o '%' (cambio relativo)
  goodDirection: 'up' | 'down';  // 'up' = subir es bueno (verde) · 'down' = bajar es bueno (verde)
  label: string;
}

// Métricas que YA son porcentaje (tasa_cancelacion_pct, tasa_no_show_pct) se
// comparan en PUNTOS PORCENTUALES (resta directa), no en % relativo: "la
// cancelación subió 25%" es ambiguo (¿25 puntos o 25% relativo sobre una base
// que ya es %?), mientras que "subió 2.3pp" es inequívoco. Métricas de conteo
// (total_consultas, pacientes_nuevos, pacientes_activos_totales) sí usan %
// relativo estándar ((actual - previo) / previo).
const buildTrend = (
  current: number | null | undefined,
  previous: number | null | undefined,
  opts: { kind: 'count' | 'rate'; goodDirection?: 'up' | 'down'; label: string },
): TrendEntry | null => {
  if (current === null || current === undefined || previous === null || previous === undefined) return null;
  const goodDirection = opts.goodDirection ?? 'up';
  if (opts.kind === 'rate') {
    return { value: current - previous, suffix: ' pp', goodDirection, label: opts.label };
  }
  if (previous === 0) {
    // Cambio % indefinido (división por cero) si el previo es 0 y el actual no
    // — se omite el trend en vez de mostrar un número engañoso (ej. "+∞%").
    if (current === 0) return { value: 0, suffix: '%', goodDirection, label: opts.label };
    return null;
  }
  return { value: ((current - previous) / previous) * 100, suffix: '%', goodDirection, label: opts.label };
};

// ============================================================================
// SUB-COMPONENTES DE PRESENTACIÓN
// ============================================================================

const TrendBadge: React.FC<{ trend: TrendEntry }> = ({ trend }) => {
  const isFlat = Math.abs(trend.value) < 0.05;
  const isUp = trend.value > 0;
  const isGood = !isFlat && (trend.goodDirection === 'up' ? isUp : !isUp);
  const color = isFlat ? 'var(--text-muted)' : isGood ? 'var(--apple-green)' : 'var(--error-text, #C4281B)';
  const arrow = isFlat ? '—' : isUp ? '▲' : '▼';
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, fontSize: 11.5, lineHeight: 1.3 }}>
      <span style={{ color, fontWeight: 700, whiteSpace: 'nowrap' }}>
        {arrow} {Math.abs(trend.value).toFixed(1)}{trend.suffix}
      </span>
      <span style={{ color: 'var(--text-muted)' }}>{trend.label}</span>
    </div>
  );
};

// Lleva la vista a la sección que desglosa este KPI y la resalta un instante.
// El dashboard del director YA muestra todo el detalle en la misma página
// (charts / breakdowns / tablas) — el click en la card es un atajo de
// navegación, no abre un modal como en el portal del terapeuta.
// Timeout de flash en curso por elemento — clickear la misma card 2 veces
// antes de que termine el flash anterior no debe cortarlo a mitad de camino.
const flashTimeouts = new WeakMap<HTMLElement, number>();

const scrollToDetail = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const prevTimeout = flashTimeouts.get(el);
  if (prevTimeout) window.clearTimeout(prevTimeout);

  // Sacar la clase y forzar reflow antes de reponerla: si ya estaba puesta,
  // classList.add() es no-op y la animation-keyframe no reinicia.
  el.classList.remove('dov-detail--flash');
  void el.offsetWidth;
  el.classList.add('dov-detail--flash');

  const timeoutId = window.setTimeout(() => {
    el.classList.remove('dov-detail--flash');
    flashTimeouts.delete(el);
  }, 1400);
  flashTimeouts.set(el, timeoutId);
};

const KpiCard: React.FC<{
  label: string;
  value: string;
  secondary?: string;
  icon: string;
  color: string;
  bg: string;
  trend?: TrendEntry[];
  /** Id del bloque que desglosa este número. Si se pasa, la card es clickeable. */
  detailTargetId?: string;
  detailHint?: string;
}> = ({ label, value, secondary, icon, color, bg, trend, detailTargetId, detailHint }) => {
  const clickable = Boolean(detailTargetId);
  const inner = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{
          fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.6px',
        }}>
          {label}
        </span>
        <div style={{
          width: 34, height: 34, borderRadius: 10,
          background: bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <i className={icon} style={{ color, fontSize: 15 }} />
        </div>
      </div>
      <div>
        <div style={{
          fontSize: 30, fontWeight: 700, color: 'var(--text-primary)',
          letterSpacing: '-0.8px', lineHeight: 1,
        }}>
          {value}
        </div>
        {secondary && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.3 }}>
            {secondary}
          </div>
        )}
        {trend && trend.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 6 }}>
            {trend.map((t, i) => <TrendBadge key={i} trend={t} />)}
          </div>
        )}
        {clickable && (
          <div style={{
            fontSize: 11, fontWeight: 600, color: 'var(--apple-blue)',
            marginTop: 8, display: 'flex', alignItems: 'center', gap: 5,
          }}>
            {detailHint ?? 'Ver desglose'} <i className="fas fa-arrow-down" style={{ fontSize: 9 }} />
          </div>
        )}
      </div>
    </>
  );

  const baseStyle: React.CSSProperties = {
    background: 'var(--bg-primary)',
    borderRadius: 16,
    padding: '18px 20px 16px',
    boxShadow: 'var(--shadow-card)',
    border: '1px solid var(--separator)',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };

  if (!clickable) return <div style={baseStyle}>{inner}</div>;

  return (
    <button
      type="button"
      className="dov-kpi-card--clickable"
      onClick={() => scrollToDetail(detailTargetId!)}
      style={{ ...baseStyle, width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer' }}
    >
      {inner}
    </button>
  );
};

const BreakdownCard: React.FC<{
  title: string;
  icon: string;
  rows: { label: string; value: number }[];
  emptyLabel: string;
}> = ({ title, icon, rows, emptyLabel }) => {
  const total = rows.reduce((sum, r) => sum + r.value, 0);
  return (
    <div style={{
      background: 'var(--bg-primary)',
      borderRadius: 16,
      padding: '18px 20px',
      boxShadow: 'var(--shadow-card)',
      border: '1px solid var(--separator)',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className={icon} style={{ color: 'var(--apple-blue)', fontSize: 14 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</span>
      </div>
      {total === 0 ? (
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{emptyLabel}</span>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map(r => (
            <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', flex: 1 }}>{r.label}</span>
              <div style={{ flex: 2, height: 6, borderRadius: 3, background: 'var(--bg-secondary)', overflow: 'hidden' }}>
                <div style={{
                  width: `${total > 0 ? (r.value / total) * 100 : 0}%`,
                  height: '100%',
                  background: 'var(--apple-blue)',
                  borderRadius: 3,
                }} />
              </div>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', width: 28, textAlign: 'right' }}>
                {r.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Fase 4 (§8, §9): gráfico de línea "Consultas por semana" — recibe la data
// ya mapeada (label corto + total/completadas/canceladas) para no acoplar
// este componente de presentación al shape crudo de ConsultaSemanaRow. Si
// `data` viene vacío (RPC sin el campo todavía porque la migración de serie
// semanal no corrió, o rango sin ninguna consulta) muestra `emptyLabel` en
// vez de un chart vacío — mismo criterio que BreakdownCard (chequeo por
// "hay algo que mostrar" antes de montar el chart).
const WeeklyConsultasChart: React.FC<{
  data: { label: string; total: number; completadas: number; canceladas: number }[];
  emptyLabel: string;
}> = ({ data, emptyLabel }) => {
  const hasData = data.some(d => d.total > 0);
  if (!hasData) {
    return <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{emptyLabel}</span>;
  }
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--separator)" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
          axisLine={{ stroke: 'var(--separator)' }}
          tickLine={false}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
          axisLine={false}
          tickLine={false}
          width={30}
        />
        <Tooltip
          contentStyle={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--separator)',
            borderRadius: 10,
            fontSize: 12.5,
          }}
          labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Line type="monotone" dataKey="total" name="Total" stroke={SEMANA_LINE_COLORS.total} strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="completadas" name="Completadas" stroke={SEMANA_LINE_COLORS.completadas} strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="canceladas" name="Canceladas" stroke={SEMANA_LINE_COLORS.canceladas} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Fase 4 (§8, §9): reemplaza el bar horizontal custom de BreakdownCard por un
// PieChart real de recharts para "Consultas por tipo" y "Consultas por
// modalidad" — mismo dato (`rows: {label, value}[]`), ahora visualizado como
// torta + lista lateral. La lista lateral se conserva a propósito (no se
// pierde información respecto al BreakdownCard anterior: label + valor
// siguen ahí, y se agrega el % que antes no se mostraba explícitamente).
const PieBreakdownCard: React.FC<{
  title: string;
  icon: string;
  rows: { label: string; value: number }[];
  colors: string[];
  emptyLabel: string;
}> = ({ title, icon, rows, colors, emptyLabel }) => {
  const total = rows.reduce((sum, r) => sum + r.value, 0);
  return (
    <div style={{
      background: 'var(--bg-primary)',
      borderRadius: 16,
      padding: '18px 20px',
      boxShadow: 'var(--shadow-card)',
      border: '1px solid var(--separator)',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className={icon} style={{ color: 'var(--apple-blue)', fontSize: 14 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</span>
      </div>
      {total === 0 ? (
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{emptyLabel}</span>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <ResponsiveContainer width={150} height={150} minWidth={130}>
            <PieChart>
              <Pie
                data={rows}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={38}
                outerRadius={62}
                paddingAngle={2}
              >
                {rows.map((r, i) => (
                  <Cell key={r.label} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--separator)',
                  borderRadius: 10,
                  fontSize: 12.5,
                }}
                formatter={(value, name) => [fmtNum(Number(value)), String(name)]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 130 }}>
            {rows.map((r, i) => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  width: 9, height: 9, borderRadius: '50%',
                  background: colors[i % colors.length], flexShrink: 0,
                }} />
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', flex: 1 }}>{r.label}</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{r.value}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', width: 34, textAlign: 'right' }}>
                  {total > 0 ? Math.round((r.value / total) * 100) : 0}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CardPanel: React.FC<{ children: React.ReactNode; id?: string }> = ({ children, id }) => (
  <div id={id} style={{
    background: 'var(--bg-primary)',
    borderRadius: 16,
    padding: '18px 20px',
    boxShadow: 'var(--shadow-card)',
    border: '1px solid var(--separator)',
  }}>
    {children}
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: '4px 0 2px' }}>
    {children}
  </h3>
);

// Selector de período (Fase 2, spec §6) + selector de terapeuta / drill-down
// (Fase 3, spec §6): misma barra de filtros, mismo patrón visual (segmented
// control + inputs con `dateInputStyle`). Estado 100% local (useState en el
// componente principal) — sin querystring, por decisión explícita del spec.
const PeriodSelector: React.FC<{
  modo: PeriodoModo;
  onModoChange: (m: PeriodoModo) => void;
  customInicio: string;
  customFin: string;
  onCustomInicioChange: (v: string) => void;
  onCustomFinChange: (v: string) => void;
  compareYoY: boolean;
  onCompareYoYChange: (v: boolean) => void;
  customRangeError: string | null;
  terapeutas: TerapeutaOption[];
  selectedTerapeutaId: string | null;
  onTerapeutaChange: (id: string | null) => void;
}> = ({
  modo, onModoChange, customInicio, customFin,
  onCustomInicioChange, onCustomFinChange,
  compareYoY, onCompareYoYChange, customRangeError,
  terapeutas, selectedTerapeutaId, onTerapeutaChange,
}) => {
  const yoyDisabled = isYoYRedundant(modo);
  const dateInputStyle: React.CSSProperties = {
    padding: '7px 10px',
    borderRadius: 8,
    border: '1px solid var(--separator)',
    fontSize: 12.5,
    color: 'var(--text-primary)',
    background: 'var(--bg-primary)',
  };
  return (
    <div style={{
      background: 'var(--bg-primary)',
      borderRadius: 16,
      padding: '14px 18px',
      boxShadow: 'var(--shadow-card)',
      border: '1px solid var(--separator)',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 16,
    }}>
      <div style={{ display: 'inline-flex', background: 'var(--bg-secondary)', borderRadius: 10, padding: 3, gap: 2 }}>
        {(Object.keys(MODO_LABEL) as PeriodoModo[]).map(m => (
          <button
            key={m}
            type="button"
            onClick={() => onModoChange(m)}
            style={{
              border: 'none',
              cursor: 'pointer',
              padding: '6px 14px',
              borderRadius: 8,
              fontSize: 12.5,
              fontWeight: 600,
              background: modo === m ? 'var(--bg-primary)' : 'transparent',
              color: modo === m ? 'var(--apple-blue)' : 'var(--text-secondary)',
              boxShadow: modo === m ? 'var(--shadow-sm)' : 'none',
            }}
          >
            {MODO_LABEL[m]}
          </button>
        ))}
      </div>

      {modo === 'personalizado' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="date"
            value={customInicio}
            onChange={e => onCustomInicioChange(e.target.value)}
            style={dateInputStyle}
            aria-label="Fecha de inicio"
          />
          <span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>–</span>
          <input
            type="date"
            value={customFin}
            onChange={e => onCustomFinChange(e.target.value)}
            style={dateInputStyle}
            aria-label="Fecha de fin"
          />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)' }}>
          Terapeuta
        </span>
        <select
          value={selectedTerapeutaId ?? ''}
          onChange={e => onTerapeutaChange(e.target.value === '' ? null : e.target.value)}
          style={{ ...dateInputStyle, cursor: 'pointer' }}
          aria-label="Filtrar por terapeuta"
        >
          <option value="">Todos los terapeutas</option>
          {terapeutas.map(t => (
            <option key={t.id} value={t.id}>{t.nombre} {t.apellido}</option>
          ))}
        </select>
      </div>

      <label
        title={yoyDisabled ? 'En modo Año, el período anterior ya es la comparación interanual.' : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12.5,
          color: yoyDisabled ? 'var(--text-muted)' : 'var(--text-secondary)',
          cursor: yoyDisabled ? 'not-allowed' : 'pointer',
          marginLeft: 'auto',
        }}
      >
        <input
          type="checkbox"
          checked={compareYoY && !yoyDisabled}
          disabled={yoyDisabled}
          onChange={e => onCompareYoYChange(e.target.checked)}
        />
        vs. año anterior (interanual)
      </label>

      {customRangeError && (
        <div style={{ width: '100%', fontSize: 12, color: 'var(--error-text, #C4281B)' }}>
          <i className="fas fa-exclamation-circle" style={{ marginRight: 5 }} />
          {customRangeError}
        </div>
      )}
    </div>
  );
};

// Tabla simple sin dependencias — mismo look que las tablas inline de
// InvestorRevenue.tsx (borderCollapse separate + spacing 0 + header var(--bg-secondary)).
const SimpleTable: React.FC<{
  headers: string[];
  rows: { key: string; cells: React.ReactNode[] }[];
  emptyLabel: string;
}> = ({ headers, rows, emptyLabel }) => {
  if (rows.length === 0) {
    return <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>{emptyLabel}</p>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>
          <tr style={{ background: 'var(--bg-secondary)' }}>
            {headers.map((h, i) => (
              <th key={h} style={{
                padding: '10px 14px',
                textAlign: i === 0 ? 'left' : 'right',
                fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap',
              }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={row.key} style={{ borderTop: ri > 0 ? '1px solid var(--separator)' : undefined }}>
              {row.cells.map((cell, ci) => (
                <td key={ci} style={{
                  padding: '11px 14px', fontSize: 13, color: 'var(--text-secondary)',
                  textAlign: ci === 0 ? 'left' : 'right',
                }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Fase 5 (§5 D, §8): formulario "+ Agregar gasto" — mes (siempre mensual,
// independiente del PeriodoModo general del dashboard, ver comentario de
// cabecera), canal (4 opciones fijas de CANAL_ORDEN), monto y notas
// opcionales. Extraído como sub-componente (igual criterio que
// `PeriodSelector`) por la cantidad de inputs controlados — el estado y el
// submit viven en el componente principal, este solo recibe value/onChange.
const GastoMarketingForm: React.FC<{
  mes: string;
  onMesChange: (v: string) => void;
  canal: GastoMarketingRow['canal'];
  onCanalChange: (v: GastoMarketingRow['canal']) => void;
  monto: string;
  onMontoChange: (v: string) => void;
  notas: string;
  onNotasChange: (v: string) => void;
  submitting: boolean;
  submitError: string | null;
  onSubmit: (e: React.FormEvent) => void;
}> = ({
  mes, onMesChange, canal, onCanalChange, monto, onMontoChange,
  notas, onNotasChange, submitting, submitError, onSubmit,
}) => {
  const inputStyle: React.CSSProperties = {
    padding: '7px 10px',
    borderRadius: 8,
    border: '1px solid var(--separator)',
    fontSize: 12.5,
    color: 'var(--text-primary)',
    background: 'var(--bg-primary)',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4, display: 'block',
  };
  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 14 }}>
      <div>
        <label style={labelStyle}>Mes</label>
        <input
          type="month"
          value={mes}
          onChange={e => onMesChange(e.target.value)}
          style={inputStyle}
          required
        />
      </div>
      <div>
        <label style={labelStyle}>Canal</label>
        <select
          value={canal}
          onChange={e => onCanalChange(e.target.value as GastoMarketingRow['canal'])}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          {CANAL_ORDEN.map(c => <option key={c} value={c}>{CANAL_LABEL[c]}</option>)}
        </select>
      </div>
      <div>
        <label style={labelStyle}>Monto (CLP)</label>
        <input
          type="number"
          min={0}
          step={1}
          value={monto}
          onChange={e => onMontoChange(e.target.value)}
          style={{ ...inputStyle, width: 130 }}
          required
        />
      </div>
      <div style={{ flex: 1, minWidth: 160 }}>
        <label style={labelStyle}>Notas (opcional)</label>
        <input
          type="text"
          value={notas}
          onChange={e => onNotasChange(e.target.value)}
          style={{ ...inputStyle, width: '100%' }}
          placeholder="ej. campaña remarketing julio"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        style={{
          padding: '8px 16px',
          borderRadius: 8,
          border: 'none',
          background: 'var(--apple-blue)',
          color: 'var(--text-white)',
          fontSize: 12.5,
          fontWeight: 600,
          cursor: submitting ? 'not-allowed' : 'pointer',
          opacity: submitting ? 0.6 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        {submitting ? 'Guardando…' : '+ Agregar gasto'}
      </button>
      {submitError && (
        <div style={{ width: '100%', fontSize: 12, color: 'var(--error-text, #C4281B)' }}>
          <i className="fas fa-exclamation-circle" style={{ marginRight: 5 }} />
          {submitError}
        </div>
      )}
    </form>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const DirectorOverview: React.FC = () => {
  // ── Selector de período (Fase 2) — estado local, sin querystring (spec §6) ──
  const [modo, setModo] = useState<PeriodoModo>('mes');
  const [customInicio, setCustomInicio] = useState<string>(() => getCurrentMonthRange().fecha_inicio);
  const [customFin, setCustomFin] = useState<string>(() => getCurrentMonthRange().fecha_fin);
  const [compareYoY, setCompareYoY] = useState(false);

  // ── Selector de terapeuta / drill-down (Fase 3) — `null` = "Todos los
  // terapeutas" (agregado clínica, default). Lista propia, independiente del
  // período — se carga una sola vez al montar (tabla de catálogo, no cambia
  // según el rango de fechas elegido).
  const [terapeutas, setTerapeutas] = useState<TerapeutaOption[]>([]);
  const [selectedTerapeutaId, setSelectedTerapeutaId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const loadTerapeutas = async () => {
      const { data, error } = await supabase
        .from('terapeutas')
        .select('id, nombre, apellido')
        .eq('activo', true)
        .order('apellido', { ascending: true })
        .order('nombre', { ascending: true });
      if (!active) return;
      if (error) {
        // Lectura de catálogo, no crítica para los KPIs — se degrada a "solo
        // Todos" en vez de romper el dashboard completo (mismo criterio que
        // el resto del componente: nunca exponer detalle de error de Supabase).
        console.error('Error cargando lista de terapeutas:', error.code);
        setTerapeutas([]);
        return;
      }
      setTerapeutas((data ?? []) as TerapeutaOption[]);
    };
    loadTerapeutas();
    return () => { active = false; };
  }, []);

  const selectedTerapeuta = useMemo(
    () => (selectedTerapeutaId ? terapeutas.find(t => t.id === selectedTerapeutaId) ?? null : null),
    [selectedTerapeutaId, terapeutas],
  );

  const [stats, setStats] = useState<DirectorDashboardStats | null>(null);
  const [prevStats, setPrevStats] = useState<DirectorDashboardStats | null>(null);
  const [yoyStats, setYoyStats] = useState<DirectorDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const currentRange = useMemo((): FechaRango => {
    switch (modo) {
      case 'mes': return getCurrentMonthRange();
      case 'trimestre': return getCurrentQuarterRange();
      case 'año': return getCurrentYearRange();
      case 'personalizado': return { fecha_inicio: customInicio, fecha_fin: customFin };
    }
  }, [modo, customInicio, customFin]);

  const customRangeError = useMemo((): string | null => {
    if (modo !== 'personalizado') return null;
    if (!customInicio || !customFin) return 'Seleccioná fecha de inicio y de fin.';
    if (customInicio > customFin) return 'La fecha de inicio debe ser anterior o igual a la fecha de fin.';
    return null;
  }, [modo, customInicio, customFin]);

  // Comparación automática vs. período anterior inmediato (spec §6, siempre activa).
  const previousRange = useMemo(
    () => getPreviousRange(modo, currentRange),
    [modo, currentRange.fecha_inicio, currentRange.fecha_fin],
  );

  // El checkbox YoY se ignora en modo Año (rango idéntico al anterior inmediato — ver isYoYRedundant).
  const effectiveCompareYoY = compareYoY && !isYoYRedundant(modo);

  const yoyRange = useMemo(
    () => getYoYRange(currentRange),
    [currentRange.fecha_inicio, currentRange.fecha_fin],
  );

  useEffect(() => {
    if (customRangeError) {
      // Rango personalizado incompleto/inválido — no se llama a la RPC todavía.
      setLoading(false);
      setStats(null);
      setPrevStats(null);
      setYoyStats(null);
      return;
    }

    let active = true;
    const load = async () => {
      setLoading(true);
      setErrorMsg(null);

      // 2 llamadas siempre (rango actual + anterior inmediato) + 1 opcional
      // (YoY, si el checkbox está activo) — todas en PARALELO (Promise.all),
      // nunca secuenciales, para no triplicar el tiempo de carga (spec §9).
      const calls = [
        supabase.rpc('get_director_dashboard_stats', {
          p_fecha_inicio: currentRange.fecha_inicio,
          p_fecha_fin: currentRange.fecha_fin,
          p_terapeuta_id: selectedTerapeutaId,
        }),
        supabase.rpc('get_director_dashboard_stats', {
          p_fecha_inicio: previousRange.fecha_inicio,
          p_fecha_fin: previousRange.fecha_fin,
          p_terapeuta_id: selectedTerapeutaId,
        }),
      ];
      if (effectiveCompareYoY) {
        calls.push(supabase.rpc('get_director_dashboard_stats', {
          p_fecha_inicio: yoyRange.fecha_inicio,
          p_fecha_fin: yoyRange.fecha_fin,
          p_terapeuta_id: selectedTerapeutaId,
        }));
      }

      const results = await Promise.all(calls);
      if (!active) return;

      const [curRes, prevRes] = results;
      const yoyRes = effectiveCompareYoY ? results[2] : null;

      if (curRes.error || prevRes.error || (yoyRes && yoyRes.error)) {
        console.error(
          'Error cargando resumen clínico del director:',
          curRes.error?.code ?? prevRes.error?.code ?? yoyRes?.error?.code,
        );
        setErrorMsg('No se pudo cargar el resumen clínico. Intentá nuevamente en unos minutos.');
        setStats(null);
        setPrevStats(null);
        setYoyStats(null);
      } else {
        setStats(curRes.data as DirectorDashboardStats);
        setPrevStats(prevRes.data as DirectorDashboardStats);
        setYoyStats(yoyRes ? (yoyRes.data as DirectorDashboardStats) : null);
      }
      setLoading(false);
    };
    load();
    return () => { active = false; };
  }, [
    currentRange.fecha_inicio, currentRange.fecha_fin,
    previousRange.fecha_inicio, previousRange.fecha_fin,
    yoyRange.fecha_inicio, yoyRange.fecha_fin,
    effectiveCompareYoY, customRangeError, selectedTerapeutaId,
  ]);

  // ── Ranking de terapeutas (Fase 5, §5 E) ─────────────────────────────────
  // useEffect propio, independiente del de stats/prevStats/yoyStats: no hay
  // comparación vs. período anterior acá (spec §5 E no la pide) y la RPC no
  // acepta p_terapeuta_id (ver comentario de cabecera) — por eso NO depende de
  // `selectedTerapeutaId`, a diferencia del useEffect de arriba. El drill-down
  // de terapeuta de la barra de filtros se ignora a propósito en esta sección.
  const [ranking, setRanking] = useState<TherapistRankingRow[]>([]);
  const [rankingLoading, setRankingLoading] = useState(true);
  const [rankingError, setRankingError] = useState<string | null>(null);

  useEffect(() => {
    if (customRangeError) {
      setRankingLoading(false);
      setRanking([]);
      return;
    }
    let active = true;
    const loadRanking = async () => {
      setRankingLoading(true);
      const { data, error } = await supabase.rpc('get_director_therapist_ranking', {
        p_fecha_inicio: currentRange.fecha_inicio,
        p_fecha_fin: currentRange.fecha_fin,
      });
      if (!active) return;
      if (error) {
        console.error('Error cargando ranking de terapeutas:', error.code);
        setRankingError('No se pudo cargar el ranking de terapeutas. Intentá nuevamente en unos minutos.');
        setRanking([]);
      } else {
        setRankingError(null);
        setRanking((data as TherapistRankingRow[]) ?? []);
      }
      setRankingLoading(false);
    };
    loadRanking();
    return () => { active = false; };
  }, [currentRange.fecha_inicio, currentRange.fecha_fin, customRangeError]);

  // ── Conciliación de pagos (FUNC-083 follow-up) ───────────────────────────
  // Query de sql/migrations/distribucion_pago_trigger_2026_08_27.sql §5-a,
  // expuesta vía RPC `get_pagos_sin_distribucion_director()`. No depende del
  // período seleccionado ni de `selectedTerapeutaId` — es un chequeo de
  // integridad del split terapeuta/inversionista sobre TODA la clínica
  // (detecta cuando `trg_dispara_distribucion_pago` falló en silencio, único
  // rastro previo era un `RAISE WARNING` en logs de Postgres que nadie mira).
  const [reconciliacion, setReconciliacion] = useState<PagoSinDistribucionRow[]>([]);
  const [reconciliacionLoading, setReconciliacionLoading] = useState(true);
  const [reconciliacionError, setReconciliacionError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const loadReconciliacion = async () => {
      setReconciliacionLoading(true);
      const { data, error } = await supabase.rpc('get_pagos_sin_distribucion_director');
      if (!active) return;
      if (error) {
        console.error('Error cargando conciliación de pagos:', error.code);
        setReconciliacionError('No se pudo cargar la conciliación de pagos. Intentá nuevamente en unos minutos.');
        setReconciliacion([]);
      } else {
        setReconciliacionError(null);
        setReconciliacion((data as PagoSinDistribucionRow[]) ?? []);
      }
      setReconciliacionLoading(false);
    };
    loadReconciliacion();
    return () => { active = false; };
  }, []);

  // ── Marketing / CAC (Fase 5, §5 D) ───────────────────────────────────────
  // Igual que el ranking: useEffect propio, sin `selectedTerapeutaId` en las
  // dependencias — ninguna de las 3 RPC de esta sección (get_gastos_marketing,
  // get_director_finanzas_stats, upsert_gasto_marketing) acepta p_terapeuta_id,
  // el gasto de marketing es agregado de TODA la clínica, no se atribuye a un
  // terapeuta (spec §5 D).
  const [gastos, setGastos] = useState<GastoMarketingRow[]>([]);
  const [gastosLoading, setGastosLoading] = useState(true);
  const [gastosError, setGastosError] = useState<string | null>(null);
  const [ingresosRango, setIngresosRango] = useState<number | null>(null);
  // `gastoRefreshKey` fuerza un refetch tras un upsert exitoso sin duplicar la
  // lógica de carga en el submit handler — mismo patrón simple que un
  // "refresh token" incremental.
  const [gastoRefreshKey, setGastoRefreshKey] = useState(0);

  useEffect(() => {
    if (customRangeError) {
      setGastosLoading(false);
      setGastos([]);
      setIngresosRango(null);
      return;
    }
    let active = true;
    const loadMarketing = async () => {
      setGastosLoading(true);
      // Paralelo (Promise.all): no dependen entre sí — mismo criterio ya
      // aplicado en el useEffect de stats/prevStats/yoyStats más arriba.
      const [gastosRes, finanzasRes] = await Promise.all([
        supabase.rpc('get_gastos_marketing', {
          p_fecha_inicio: currentRange.fecha_inicio,
          p_fecha_fin: currentRange.fecha_fin,
        }),
        supabase.rpc('get_director_finanzas_stats', {
          p_fecha_inicio: currentRange.fecha_inicio,
          p_fecha_fin: currentRange.fecha_fin,
        }),
      ]);
      if (!active) return;
      if (gastosRes.error || finanzasRes.error) {
        console.error(
          'Error cargando marketing/CAC:',
          gastosRes.error?.code ?? finanzasRes.error?.code,
        );
        setGastosError('No se pudo cargar el módulo de marketing. Intentá nuevamente en unos minutos.');
        setGastos([]);
        setIngresosRango(null);
      } else {
        setGastosError(null);
        setGastos((gastosRes.data as GastoMarketingRow[]) ?? []);
        setIngresosRango((finanzasRes.data as DirectorFinanzasStatsSlim | null)?.ingresos_rango ?? null);
      }
      setGastosLoading(false);
    };
    loadMarketing();
    return () => { active = false; };
  }, [currentRange.fecha_inicio, currentRange.fecha_fin, customRangeError, gastoRefreshKey]);

  // Estado del formulario "+ Agregar gasto" — mes SIEMPRE mensual (input
  // type="month", formato "YYYY-MM"), independiente del `modo` general del
  // dashboard (spec §5 D / comentario de cabecera).
  const [gastoMes, setGastoMes] = useState<string>(() => {
    const hoy = new Date();
    return `${hoy.getFullYear()}-${pad2(hoy.getMonth() + 1)}`;
  });
  const [gastoCanal, setGastoCanal] = useState<GastoMarketingRow['canal']>('meta_ads');
  const [gastoMonto, setGastoMonto] = useState('');
  const [gastoNotas, setGastoNotas] = useState('');
  const [gastoSubmitting, setGastoSubmitting] = useState(false);
  const [gastoSubmitError, setGastoSubmitError] = useState<string | null>(null);

  const handleGastoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGastoSubmitError(null);

    const montoNum = Number(gastoMonto);
    if (!gastoMes || gastoMonto.trim() === '' || Number.isNaN(montoNum) || montoNum < 0) {
      setGastoSubmitError('Completá mes, canal y un monto válido (mayor o igual a cero).');
      return;
    }

    setGastoSubmitting(true);
    // `gastoMes` viene como "YYYY-MM" del <input type="month"> — se completa
    // al día 1 para mandar un DATE válido (la RPC igual normaliza al día 1
    // server-side, esto solo evita depender de esa normalización implícita).
    const { data, error } = await supabase.rpc('upsert_gasto_marketing', {
      p_mes: `${gastoMes}-01`,
      p_canal: gastoCanal,
      p_monto: montoNum,
      p_notas: gastoNotas.trim() === '' ? null : gastoNotas.trim(),
    });
    setGastoSubmitting(false);

    if (error || !data) {
      // Mensaje genérico — nunca error.message ni nombre de columna/schema al
      // usuario (mismo criterio que el resto del archivo). rol='director' ya
      // lo garantiza la RPC server-side (spec §4) — no se duplica esa
      // validación acá.
      console.error('Error registrando gasto de marketing:', error?.code);
      setGastoSubmitError('No se pudo registrar el gasto. Intentá nuevamente.');
      return;
    }

    setGastoMonto('');
    setGastoNotas('');
    setGastoRefreshKey(k => k + 1);
  };

  const gastoTotalPeriodo = gastos[0]?.gasto_total_periodo ?? 0;
  const cacClinica = gastos[0]?.cac_clinica ?? null;
  // ROI simple = ingresos del período ÷ gasto marketing del período (spec §5 D).
  // "—" si no hay gasto cargado (gasto_total_periodo=0 o array vacío, ver
  // comentario de cabecera) — dividir por cero mostraría un múltiplo infinito
  // engañoso, mismo criterio defensivo que buildTrend con previous=0 más arriba.
  const roiSimple = gastoTotalPeriodo > 0 && ingresosRango !== null
    ? ingresosRango / gastoTotalPeriodo
    : null;

  const periodoLabel = useMemo(() => {
    const inicio = parseISODate(currentRange.fecha_inicio);
    const fin = parseISODate(currentRange.fecha_fin);
    switch (modo) {
      case 'mes': {
        const label = inicio.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
        return label.charAt(0).toUpperCase() + label.slice(1);
      }
      case 'trimestre':
        return `Trimestre ${Math.floor(inicio.getMonth() / 3) + 1} · ${inicio.getFullYear()}`;
      case 'año':
        return `Año ${inicio.getFullYear()}`;
      case 'personalizado': {
        const fmt = (d: Date) => d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
        return `${fmt(inicio)} – ${fmt(fin)}`;
      }
    }
  }, [modo, currentRange.fecha_inicio, currentRange.fecha_fin]);

  // Helper de armado de trend por KPI: combina el delta vs. período anterior
  // inmediato (siempre, si hay datos) + el delta YoY (solo si el checkbox
  // está activo) — ambos se muestran a la vez, uno NO reemplaza al otro
  // (spec §9 Fase 2, punto 3).
  const trendFor = (
    metric: (s: DirectorDashboardStats) => number | null | undefined,
    opts: { kind: 'count' | 'rate'; goodDirection?: 'up' | 'down' },
  ): TrendEntry[] => {
    const entries: TrendEntry[] = [];
    if (stats && prevStats) {
      const t = buildTrend(metric(stats), metric(prevStats), { ...opts, label: PREV_LABEL[modo] });
      if (t) entries.push(t);
    }
    if (effectiveCompareYoY && stats && yoyStats) {
      const t = buildTrend(metric(stats), metric(yoyStats), { ...opts, label: YOY_LABEL[modo] });
      if (t) entries.push(t);
    }
    return entries;
  };

  // El header y el selector de período siempre se muestran (incluso durante
  // loading/error/rango inválido) para que el director nunca quede "atrapado"
  // sin forma de corregir un rango personalizado inválido — solo el cuerpo
  // de KPIs cambia según el estado.
  let body: React.ReactNode;

  if (loading) {
    body = (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner" />
          <p>Cargando resumen clínico...</p>
        </div>
      </div>
    );
  } else if (errorMsg) {
    body = (
      <div style={{
        padding: '32px 24px', background: 'var(--bg-secondary)', borderRadius: 16,
        textAlign: 'center', border: '1px solid var(--separator)',
      }}>
        <i className="fas fa-exclamation-triangle" style={{
          fontSize: 28, color: 'var(--apple-orange)', marginBottom: 10, display: 'block',
        }} />
        <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: 14, lineHeight: 1.6 }}>
          {errorMsg}
        </p>
      </div>
    );
  } else if (!stats) {
    // customRangeError sin RPC llamada todavía — el mensaje ya se muestra
    // dentro de PeriodSelector, no hay nada más que renderizar acá.
    body = null;
  } else {
    const { operacionales: op, growth: gr } = stats;

    const consultasPorEstado = op.consultas_por_estado ?? {};
    const completadas = consultasPorEstado['completada'] ?? 0;
    const canceladas = consultasPorEstado['cancelada'] ?? 0;
    const noShow = consultasPorEstado['no_asistio'] ?? 0;

    const consultasPorTipoRows = TIPO_ORDEN.map(k => ({
      label: TIPO_LABEL[k], value: (op.consultas_por_tipo ?? {})[k] ?? 0,
    }));
    const consultasPorModalidadRows = MODALIDAD_ORDEN.map(k => ({
      label: MODALIDAD_LABEL[k], value: (op.consultas_por_modalidad ?? {})[k] ?? 0,
    }));

    // Fase 4 (§8): data del line chart "Consultas por semana" — `?? []` cubre
    // tanto el caso "rango sin consultas" como "la migración de serie semanal
    // todavía no corrió en Supabase" (el campo directamente no viene en el
    // JSON de la RPC en ese caso).
    const semanaChartData = (op.consultas_por_semana ?? []).map(w => ({
      label: fmtSemanaCorta(w.semana_inicio),
      total: w.total,
      completadas: w.completadas,
      canceladas: w.canceladas,
    }));

    const origenPacientesRows = [
      { label: 'Referidos por inversionista', value: gr.pacientes_referidos_inversionista ?? 0 },
      { label: 'Captación orgánica / directa', value: gr.pacientes_organicos ?? 0 },
    ];

    const ocupacionRows = (op.ocupacion_por_terapeuta ?? []).map(t => ({
      key: t.terapeuta_id,
      cells: [
        `${t.terapeuta_nombre} ${t.terapeuta_apellido}`,
        fmtNum(t.completadas),
        fmtNum(t.slots_disponibles),
        fmtPct(t.ocupacion_pct),
      ],
    }));

    const pacientesPorTerapeutaRows = (gr.pacientes_por_terapeuta ?? []).map(t => ({
      key: t.terapeuta_id,
      cells: [
        `${t.terapeuta_nombre} ${t.terapeuta_apellido}`,
        fmtNum(t.total_pacientes),
      ],
    }));

    // Fase 5 (§5 E): filas del ranking de terapeutas. El "#N" junto a cada
    // valor es el puesto de ese ranking específico (ranking_ingresos ≠
    // ranking_ocupacion ≠ ranking_cancelaciones — un terapeuta puede ser #1 en
    // ingresos y #3 en ocupación a la vez) — mejora la legibilidad sin agregar
    // columnas nuevas a `SimpleTable` (spec §8 no obliga a mostrarlo, decisión
    // de diseño de esta fase).
    const rankBadge = (rank: number) => (
      <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 5 }}>#{rank}</span>
    );
    const rankingRows = ranking.map(r => ({
      key: r.terapeuta_id,
      cells: [
        `${r.terapeuta_nombre} ${r.terapeuta_apellido}`,
        <>{fmtCLP(r.ingresos_periodo)}{rankBadge(r.ranking_ingresos)}</>,
        <>{fmtPct(r.ocupacion_pct)}{rankBadge(r.ranking_ocupacion)}</>,
        <>{fmtPct(r.cancelacion_no_show_pct)}{rankBadge(r.ranking_cancelaciones)}</>,
      ],
    }));

    // Fase 5 (§5 D): filas de la tabla de gastos de marketing — solo
    // Mes/Canal/Monto (sin columna de notas: SimpleTable alinea a la derecha
    // todo lo que no sea la primera columna, y un texto libre right-aligned
    // se lee mal; las notas quedan guardadas en BD para contexto futuro del
    // director, no hace falta mostrarlas en esta tabla resumen).
    const gastosRows = gastos.map(g => ({
      key: g.id,
      cells: [fmtMesLabel(g.mes), CANAL_LABEL[g.canal] ?? g.canal, fmtCLP(g.monto)],
    }));

    body = (
      <>
        {/* ── KPIs Operacionales ── */}
        <SectionTitle>Operación</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 14 }}>
          <KpiCard
            label="Consultas del período"
            value={fmtNum(op.total_consultas)}
            secondary={`${completadas} completadas · ${canceladas} canceladas · ${noShow} no-show`}
            icon="fas fa-calendar-check"
            color="var(--apple-blue)"
            bg="rgba(0, 122, 255, 0.10)"
            trend={trendFor(s => s.operacionales.total_consultas, { kind: 'count', goodDirection: 'up' })}
            detailTargetId="dov-sec-consultas-semana"
            detailHint="Ver por semana y tipo"
          />
          <KpiCard
            label="Tasa de cancelación"
            value={fmtPct(op.tasa_cancelacion_pct)}
            secondary="sobre el total de consultas del período"
            icon="fas fa-calendar-times"
            color="var(--error-text, #C4281B)"
            bg="rgba(255, 59, 48, 0.10)"
            trend={trendFor(s => s.operacionales.tasa_cancelacion_pct, { kind: 'rate', goodDirection: 'down' })}
            detailTargetId="dov-sec-ranking"
            detailHint="Ver por terapeuta"
          />
          <KpiCard
            label="Tasa de no-show"
            value={fmtPct(op.tasa_no_show_pct)}
            secondary="pacientes que no se presentaron"
            icon="fas fa-user-clock"
            color="var(--apple-orange)"
            bg="rgba(255, 149, 0, 0.10)"
            trend={trendFor(s => s.operacionales.tasa_no_show_pct, { kind: 'rate', goodDirection: 'down' })}
            detailTargetId="dov-sec-ranking"
            detailHint="Ver por terapeuta"
          />
        </div>

        <CardPanel id="dov-sec-consultas-semana">
          <SectionTitle>Consultas por semana</SectionTitle>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 14px' }}>
            Total, completadas y canceladas por semana calendario (lunes a domingo) dentro del período seleccionado
          </p>
          <WeeklyConsultasChart
            data={semanaChartData}
            emptyLabel="Sin datos de consultas por semana en el período."
          />
        </CardPanel>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          <PieBreakdownCard
            title="Consultas por tipo"
            icon="fas fa-layer-group"
            rows={consultasPorTipoRows}
            colors={TIPO_PIE_COLORS}
            emptyLabel="Sin consultas en el período."
          />
          <PieBreakdownCard
            title="Consultas por modalidad"
            icon="fas fa-desktop"
            rows={consultasPorModalidadRows}
            colors={MODALIDAD_PIE_COLORS}
            emptyLabel="Sin consultas en el período."
          />
        </div>

        <CardPanel>
          <SectionTitle>Ocupación por terapeuta</SectionTitle>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 14px' }}>
            Consultas completadas sobre slots configurados en disponibilidad — no descuenta bloqueos de agenda (vacaciones/licencias)
          </p>
          <SimpleTable
            headers={['Terapeuta', 'Completadas', 'Slots', '% Ocupación']}
            rows={ocupacionRows}
            emptyLabel="No hay terapeutas activos con disponibilidad configurada."
          />
        </CardPanel>

        {/* ── KPIs Growth ── */}
        <SectionTitle>Crecimiento de pacientes</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 14 }}>
          <KpiCard
            label="Pacientes nuevos"
            value={fmtNum(gr.pacientes_nuevos)}
            secondary="primera consulta en el período"
            icon="fas fa-user-plus"
            color="var(--apple-green)"
            bg="rgba(52, 199, 89, 0.10)"
            trend={trendFor(s => s.growth.pacientes_nuevos, { kind: 'count', goodDirection: 'up' })}
            detailTargetId="dov-sec-origen-pacientes"
            detailHint="Ver origen"
          />
          <KpiCard
            label="Pacientes activos"
            value={fmtNum(gr.pacientes_activos_totales)}
            secondary="cartera total de la clínica"
            icon="fas fa-users"
            color="var(--apple-blue)"
            bg="rgba(0, 122, 255, 0.10)"
            trend={trendFor(s => s.growth.pacientes_activos_totales, { kind: 'count', goodDirection: 'up' })}
            detailTargetId="dov-sec-pacientes-terapeuta"
            detailHint="Ver por terapeuta"
          />
        </div>

        <div id="dov-sec-origen-pacientes" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          <BreakdownCard
            title="Origen de pacientes nuevos"
            icon="fas fa-handshake"
            rows={origenPacientesRows}
            emptyLabel="Sin pacientes nuevos en el período."
          />
        </div>

        <CardPanel id="dov-sec-pacientes-terapeuta">
          <SectionTitle>Pacientes por terapeuta</SectionTitle>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 14px' }}>
            Carga de cartera activa por terapeuta (snapshot actual, no filtrado por período)
          </p>
          <SimpleTable
            headers={['Terapeuta', 'Pacientes activos']}
            rows={pacientesPorTerapeutaRows}
            emptyLabel="No hay terapeutas activos con pacientes asignados."
          />
        </CardPanel>

        {/* ── Ranking terapeutas (Fase 5, §5 E) ── */}
        <CardPanel id="dov-sec-ranking">
          <SectionTitle>Ranking terapeutas</SectionTitle>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 14px' }}>
            Comparativo de TODOS los terapeutas activos del período — no cambia con el filtro de terapeuta de arriba (un ranking de un solo terapeuta no es comparable)
          </p>
          {rankingLoading ? (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cargando ranking...</span>
          ) : rankingError ? (
            <div style={{ fontSize: 12, color: 'var(--error-text, #C4281B)' }}>
              <i className="fas fa-exclamation-circle" style={{ marginRight: 5 }} />
              {rankingError}
            </div>
          ) : (
            <SimpleTable
              headers={['Terapeuta', 'Ingresos', '% Ocupación', 'Cancelación/no-show']}
              rows={rankingRows}
              emptyLabel="No hay terapeutas activos para comparar en el período."
            />
          )}
        </CardPanel>

        {/* ── Conciliación de pagos (FUNC-083 follow-up) ── */}
        <SectionTitle>Conciliación de pagos</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 14 }}>
          <KpiCard
            label="Pagos sin conciliar"
            value={reconciliacionLoading ? '—' : fmtNum(reconciliacion.length)}
            secondary="cobrados sin split de distribución calculado"
            icon="fas fa-exclamation-triangle"
            color="var(--apple-orange)"
            bg="rgba(255, 149, 0, 0.10)"
            detailTargetId="dov-sec-conciliacion"
            detailHint="Ver pagos"
          />
        </div>

        <CardPanel id="dov-sec-conciliacion">
          <SectionTitle>Pagos pagados sin distribución</SectionTitle>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 14px' }}>
            Pagos confirmados (estado 'pagado') que no generaron fila de distribución de ingresos — señal de que el split terapeuta/inversionista falló o no se disparó (FUNC-083). No depende del período seleccionado arriba: es un chequeo de integridad sobre toda la clínica.
          </p>
          {reconciliacionLoading ? (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cargando...</span>
          ) : reconciliacionError ? (
            <div style={{ fontSize: 12, color: 'var(--error-text, #C4281B)' }}>
              <i className="fas fa-exclamation-circle" style={{ marginRight: 5 }} />
              {reconciliacionError}
            </div>
          ) : (
            <SimpleTable
              headers={['Fecha de pago', 'Paciente', 'Terapeuta', 'Método', 'Monto']}
              rows={reconciliacion.map(r => ({
                key: r.pago_id,
                cells: [
                  r.fecha_pago
                    ? new Date(r.fecha_pago).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '—',
                  [r.paciente_nombre, r.paciente_apellido].filter(Boolean).join(' ') || '—',
                  [r.terapeuta_nombre, r.terapeuta_apellido].filter(Boolean).join(' ') || 'Sin asignar',
                  r.metodo_pago ?? '—',
                  fmtCLP(r.monto_final ?? r.monto),
                ],
              }))}
              emptyLabel="Sin pagos pendientes de conciliar — todo distribuido correctamente."
            />
          )}
        </CardPanel>

        {/* ── Marketing / CAC (Fase 5, §5 D) ── */}
        <CardPanel>
          <SectionTitle>Marketing / CAC</SectionTitle>
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: '10px 12px', borderRadius: 10,
            background: 'var(--bg-secondary)', border: '1px solid var(--separator)',
            fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, margin: '2px 0 14px',
          }}>
            <i className="fas fa-info-circle" style={{ color: 'var(--apple-orange)', marginTop: 1, flexShrink: 0 }} />
            <span>
              <strong style={{ color: 'var(--text-secondary)' }}>CAC agregado de toda la clínica</strong> — sin
              tracking UTM por paciente no es posible atribuir el costo de adquisición a un canal específico.
            </span>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'stretch' }}>
            <div style={{ flex: '2 1 360px', minWidth: 300 }}>
              <GastoMarketingForm
                mes={gastoMes}
                onMesChange={setGastoMes}
                canal={gastoCanal}
                onCanalChange={setGastoCanal}
                monto={gastoMonto}
                onMontoChange={setGastoMonto}
                notas={gastoNotas}
                onNotasChange={setGastoNotas}
                submitting={gastoSubmitting}
                submitError={gastoSubmitError}
                onSubmit={handleGastoSubmit}
              />
              {gastosLoading ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cargando gastos...</span>
              ) : gastosError ? (
                <div style={{ fontSize: 12, color: 'var(--error-text, #C4281B)' }}>
                  <i className="fas fa-exclamation-circle" style={{ marginRight: 5 }} />
                  {gastosError}
                </div>
              ) : (
                <SimpleTable
                  headers={['Mes', 'Canal', 'Monto']}
                  rows={gastosRows}
                  emptyLabel="Sin gastos de marketing cargados en el período."
                />
              )}
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 180 }}>
              <KpiCard
                label="CAC clínica"
                value={fmtCLP(cacClinica)}
                secondary="gasto ÷ pacientes nuevos del período"
                icon="fas fa-bullseye"
                color="var(--apple-purple)"
                bg="rgba(175, 82, 222, 0.10)"
              />
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 180 }}>
              <KpiCard
                label="ROI simple"
                value={fmtRatio(roiSimple)}
                secondary="ingresos del período ÷ gasto marketing"
                icon="fas fa-chart-line"
                color="var(--apple-green)"
                bg="rgba(52, 199, 89, 0.10)"
              />
            </div>
          </div>
        </CardPanel>
      </>
    );
  }

  return (
    <div className="dashboard-home">
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 2px' }}>
          Resumen Clínico
        </h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
          {periodoLabel} · {selectedTerapeuta
            ? `${selectedTerapeuta.nombre} ${selectedTerapeuta.apellido}`
            : 'agregado de toda la clínica'}
        </p>
      </div>

      <PeriodSelector
        modo={modo}
        onModoChange={setModo}
        customInicio={customInicio}
        customFin={customFin}
        onCustomInicioChange={setCustomInicio}
        onCustomFinChange={setCustomFin}
        compareYoY={compareYoY}
        onCompareYoYChange={setCompareYoY}
        customRangeError={customRangeError}
        terapeutas={terapeutas}
        selectedTerapeutaId={selectedTerapeutaId}
        onTerapeutaChange={setSelectedTerapeutaId}
      />

      {body}
    </div>
  );
};

export default DirectorOverview;
